import { getFileUploadMeta } from '@unifyapps/carbon/hooks/useUppy';
import type { FileVisibility } from '@unifyapps/defs/types/fileVisibility';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import { useTranslation } from '@unifyapps/i18n/client';
import { useSnackbar } from '@unifyapps/ui/components/Snackbar';
import { useContainerElementContext } from '@unifyapps/ui/providers/ContainerElementContext';
import { invariant } from 'ts-invariant';
import { addFiles } from '../../../../../components/FileInput/utils/addFiles';
import type { ActionHandlerType } from '../../../../context/ActionsProvider/context';
import { useGlobalAppStateStore } from '../../../../stores/app/GlobalAppStateStore';
import { useGetNoCodeComputedData } from '../../../computeContext/useGetNoCodeComputedData';
import { SHOULD_NOT_PROPAGATE_UP, SkipActionHandlersError } from '../../const';
import {
  useGetControlMediaUploadsReferenceId,
  useMediaUploadState,
} from './hooks/useMediaUploadState';
import type { ControlMediaUploadsActionArgs } from './types';
import { createFileMeta } from './utils/fileValidation';
import type { PickerResult } from './utils/picker';
import { openCamera, openFileUpload } from './utils/picker';
import { getPendingFileIds, waitForFilesUpload } from './utils/waitForFilesUpload';

export function useControlMediaUploads() {
  const { uppy, removeFileFromMediaUploadsState, resetMediaUploads } = useMediaUploadState();
  const referenceIdWithoutBlockId = useGetControlMediaUploadsReferenceId();
  const { getNoCodeComputedData } = useGetNoCodeComputedData();
  const containerElement = useContainerElementContext();
  const device = useGlobalAppStateStore().hooks.useDeviceDetails()?.device;
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation('common');

  const handlePickerResult = useEventCallback(
    async (
      blockId: string,
      pickerResult: PickerResult,
      operationType: 'camera' | 'fileUpload',
      fileVisibility?: FileVisibility,
      instanceId?: string,
    ): Promise<boolean> => {
      const { validFiles, errors, selectedFileCount } = pickerResult;

      if (errors.length > 0) {
        console.error(`${operationType} errors:`, errors);
      }

      // Real picker failure (permission denied, capture error) and no files came through.
      // Throw before the Case 1 cancel check so onErrorActions run instead of being skipped.
      // We also require validFiles to be empty: if some files came through, don't throw —
      // upload those below.
      if (errors.length > 0 && validFiles.length === 0) {
        throw new Error(errors.join(', '));
      }

      // Case 1: User cancelled the picker (selectedFileCount === 0) - skip success/error handlers
      if (selectedFileCount === 0) {
        throw new SkipActionHandlersError('File picker was cancelled');
      }

      // Case 2: All files were rejected due to validation (size limit, max files, etc.)
      if (validFiles.length === 0 && selectedFileCount > 0) {
        throw new Error('All files were rejected due to validation errors');
      }

      // Case 3: No uppy instance available (edge case)
      if (!uppy) {
        throw new SkipActionHandlersError('No uppy instance available');
      }

      // Case 4: Files are valid - proceed with upload
      // instanceId is stashed in meta so the async uppy completion callbacks
      // (useMediaUploadState.tsx) can route state writes back to the
      // per-instance GSS. Without it, writes land on the base store and the
      // FileUploaderField inside a Module/TemplateComponent sees empty state.
      const fileMeta = {
        ...createFileMeta(blockId, referenceIdWithoutBlockId, instanceId),
        ...getFileUploadMeta(fileVisibility),
      };
      await addFiles(validFiles, 'ControlMediaUploads', uppy, fileMeta, fileMeta.referenceId);
      const fileIds = getPendingFileIds(uppy, fileMeta.referenceId);

      if (fileIds.length) {
        const uploadResult = await waitForFilesUpload({ uppy, fileIds });

        // All uploads failed - throw error to trigger error handlers
        if (uploadResult.failedCount > 0 && uploadResult.successCount === 0) {
          throw new Error('All file uploads failed');
        }
      }

      // Success (full or partial) - let the success handlers execute
      return SHOULD_NOT_PROPAGATE_UP;
    },
  );

  const handleOpenCamera = useEventCallback(
    async (
      blockId: string,
      action: ControlMediaUploadsActionArgs['action'],
      instanceId?: string,
    ) => {
      invariant(blockId, 'Block ID is required');
      const { config } = action.payload;

      const pickerResult = await openCamera({
        ...config,
        containerElement,
        showSnackbar,
        t,
        device,
      });

      return handlePickerResult(
        blockId,
        pickerResult,
        'camera',
        config?.fileVisibility,
        instanceId,
      );
    },
  );

  const handleOpenFileUploader = useEventCallback(
    async (
      blockId: string,
      action: ControlMediaUploadsActionArgs['action'],
      instanceId?: string,
    ) => {
      invariant(blockId, 'Block ID is required');
      const { config } = action.payload;

      const pickerResult = await openFileUpload({
        ...config,
        containerElement,
        showSnackbar,
        t,
        device,
      });

      return handlePickerResult(
        blockId,
        pickerResult,
        'fileUpload',
        config?.fileVisibility,
        instanceId,
      );
    },
  );

  const handleRemoveFile = useEventCallback((fileId?: string, instanceId?: string) => {
    if (!fileId) {
      throw new Error('File ID is required for removeFile operation');
    }
    removeFileFromMediaUploadsState(fileId, instanceId);
    return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
  });

  // const handleRemoveSourceFiles = useEventCallback((sourceIds?: string[]) => {
  //   if (!sourceIds) {
  //     throw new Error('Source IDs are required for removeSourceFiles operation');
  //   }
  //   sourceIds.forEach((sourceId) => {
  //     removeSourceFromMediaUploadsState(sourceId);
  //   });
  //   return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
  // });

  const handleResetMediaUploads = useEventCallback((instanceId?: string) => {
    resetMediaUploads(instanceId);
    return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
  });

  const onControlMediaUploads: ActionHandlerType = useEventCallback((args) => {
    const payload = args.action.payload as ControlMediaUploadsActionArgs['action']['payload'];
    const instanceId = args.instanceId;
    const { operation } = payload;

    const { action: computedAction } = getNoCodeComputedData<{
      action: ControlMediaUploadsActionArgs['action'];
    }>(
      {
        action: args.action,
      },
      args.actionContext,
    );

    switch (operation) {
      case 'capturePhoto':
        invariant(args.blockId, 'Block ID is required');
        return handleOpenCamera(args.blockId, computedAction, instanceId);
      case 'uploadFiles':
        invariant(args.blockId, 'Block ID is required');
        return handleOpenFileUploader(args.blockId, computedAction, instanceId);
      case 'deleteFile':
        return handleRemoveFile(computedAction.payload?.fileId, instanceId);
      case 'resetMediaUploads':
        return handleResetMediaUploads(instanceId);
      default:
        throw new Error(`Unknown operation: ${operation}`);
    }
  });

  return { onControlMediaUploads };
}
